import { createTheme } from '@mui/material/styles';
import { COLORS, FONTS, SPACING, FIELD_WIDTHS } from './themeConstants';

export const theme = createTheme({
  palette: {
    primary: {
      main: COLORS.primary.main,
      light: COLORS.primary.light,
      dark: COLORS.primary.dark,
    },
    secondary: {
      main: COLORS.secondary.main,
      light: COLORS.secondary.light,
      dark: COLORS.secondary.dark,
    },
    text: {
      primary: COLORS.text.primary,
      secondary: COLORS.text.secondary,
    },
    background: {
      default: COLORS.background.default,
      paper: COLORS.background.paper,
    },
    error: {
      main: COLORS.error.main,
      light: COLORS.error.light,
      dark: COLORS.error.dark,
    },
    success: {
      main: COLORS.success.main,
      light: COLORS.success.light,
      dark: COLORS.success.dark,
    },
    warning: {
      main: COLORS.warning.main,
      light: COLORS.warning.light,
      dark: COLORS.warning.dark,
    },
    info: {
      main: COLORS.info.main,
      light: COLORS.info.light,
      dark: COLORS.info.dark,
    },
  },
  typography: {
    fontFamily: FONTS.family.primary,
    fontSize: parseInt(FONTS.size.sm),
    h1: {
      fontSize: FONTS.size.xxl,
      fontWeight: FONTS.weight.bold,
    },
    h2: {
      fontSize: FONTS.size.xl,
      fontWeight: FONTS.weight.bold,
    },
    h3: {
      fontSize: FONTS.size.lg,
      fontWeight: FONTS.weight.bold,
    },
    body1: {
      fontSize: FONTS.size.md,
      fontWeight: FONTS.weight.regular,
    },
    body2: {
      fontSize: FONTS.size.sm,
      fontWeight: FONTS.weight.regular,
    },
    caption: {
      fontSize: FONTS.size.xs,
      fontWeight: FONTS.weight.regular,
    },
  },
  components: {
    MuiFormControl: {
      styleOverrides: {
        root: {
          marginBottom: SPACING.sm,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          '& .MuiInputLabel-root': {
            position: 'relative',
            transform: 'none',
            width: '130px',
            color: COLORS.text.label,
            fontSize: FONTS.size.sm,
            // Use logical properties so spacing flips in RTL
            marginInlineEnd: SPACING.xs,
            // Align to the block end so it flips with direction
            textAlign: 'end',
            paddingTop: SPACING.sm,
            fontWeight: FONTS.weight.medium,
          },
          '& .MuiFormLabel-root': {
            width: '130px',
            color: COLORS.text.label,
            fontSize: FONTS.size.sm,
            marginInlineEnd: SPACING.xs,
            textAlign: 'end',
            paddingTop: SPACING.sm,
            fontWeight: FONTS.weight.medium,
          },
          '& .MuiOutlinedInput-root': {
            height: '32px',
          },
          '& .MuiSelect-select': {
            padding: `${SPACING.xs} ${SPACING.sm}`,
            minHeight: '20px',
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiFormHelperText-root': {
            position: 'absolute',
            bottom: '-20px',
            //marginLeft: '134px',
            fontSize: FONTS.size.xs,
          },
          '& .MuiRadioGroup-root': {
            flexDirection: 'row',
            gap: SPACING.md,
            // Logical start margin
            marginInlineStart: SPACING.xs,
            color: COLORS.secondary.main
          },
          '& .MuiFormGroup-root': {
            flexDirection: 'row',
            gap: SPACING.md,
            marginInlineStart: SPACING.xs,
            color: COLORS.secondary.main
          },
          '& .MuiFormControlLabel-root': {
            margin: 0,
            '& .MuiRadio-root': {
              padding: SPACING.xs,
              color: COLORS.secondary.main
            },
            '& .MuiCheckbox-root': {
              padding: SPACING.xs,
              color: COLORS.secondary.main
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: COLORS.background.paper,
          width: FIELD_WIDTHS.lg,
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: COLORS.secondary.main,
            borderWidth: '1px',
          },
  
          '&.Mui-focused fieldset': {
            borderColor: COLORS.secondary.main,
            borderWidth: '1px',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: COLORS.secondary.main,
          },
          '& input': {
            padding: `${SPACING.xs} ${SPACING.sm}`,
            height: '20px',
            '&::placeholder': {
              color: 'rgba(0, 0, 0, 0.38)',
              opacity: 1,
            },
          },
          '&.input-large': {
            width: FIELD_WIDTHS.lg,
          },
        },
      },
      variants: [
        {
          props: { size: 'small' },
          style: {
            width: FIELD_WIDTHS.xs,
          },
        },
        {
          props: { size: 'medium' },
          style: {
            width: FIELD_WIDTHS.md,
          },
        },
      ],
    },
    MuiInputLabel: {
      styleOverrides: {
        asterisk: {
          color: COLORS.error.main,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          backgroundColor: COLORS.background.paper,
          minHeight: '20px',
          display: 'flex',
          alignItems: 'center',
          padding: '4px 8px',
          '&.MuiSelect-select': {
            '&[value=""]': {
              color: COLORS.text.placeholder,
            },
          },
          '& .MuiSelect-select': {
            '&[value=""]': {
              color: COLORS.text.placeholder,
            },
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          minHeight: '32px',
          fontSize: FONTS.size.sm,
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: COLORS.secondary.main, // default color when unchecked
          '&.Mui-checked': {
            color: COLORS.secondary.main, // color when checked
          },
        },
      },
    },
  },
});
